/**
 * ProductRawDataTab V2
 *
 * Tab 7: Raw Data mit JSON Viewer und Copy Button.
 * Zeigt die Rohdaten des Produkts aus dem Provider (live und synced).
 *
 * Sektionen:
 * - Item: Live product metadata from provider
 * - Descriptions: Live descriptions from provider
 * - Images: Live images metadata from provider
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Code,
  Copy,
  ChevronDown,
  ChevronUp,
  Braces,
  Info,
  RefreshCw,
  Database,
  FileText,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';
import type { Product } from '@/types/products';
import {
  getProviderItemData,
  getProviderDescriptionsData,
  getProviderImagesData,
} from '@/lib/api/products';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Alert,
  AlertDescription,
} from '@/components/v2/ui';

interface ProductRawDataTabProps {
  product: Product;
}

export function ProductRawDataTab({ product }: ProductRawDataTabProps) {
  return (
    <div className="product-raw-data-tab space-y-4">
      {/* Info Alert */}
      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Entwickler-Ansicht:</strong> Diese Daten zeigen die Live-Antworten
          vom Provider ({product.provider_type?.toUpperCase() || 'JTL'}). Nutze dies
          um zu sehen, was der Provider zurückgibt und wo Synchronisations-Probleme
          auftreten könnten.
        </AlertDescription>
      </Alert>

      {/* Item Section */}
      <RawDataSection
        title="Item (Produkt-Metadaten)"
        icon={<Database className="h-5 w-5" />}
        productId={product.id}
        fetchFn={getProviderItemData}
        queryKey="provider-raw-item"
      />

      {/* Descriptions Section */}
      <RawDataSection
        title="Descriptions (Produkttexte)"
        icon={<FileText className="h-5 w-5" />}
        productId={product.id}
        fetchFn={getProviderDescriptionsData}
        queryKey="provider-raw-descriptions"
      />

      {/* Images Section */}
      <RawDataSection
        title="Images (Bilder-Metadaten)"
        icon={<ImageIcon className="h-5 w-5" />}
        productId={product.id}
        fetchFn={getProviderImagesData}
        queryKey="provider-raw-images"
      />

      {/* Synced Raw Data (Original) */}
      <RawDataSection
        title="Synced Data (Letzte Synchronisation)"
        icon={<Code className="h-5 w-5" />}
        productId={product.id}
        staticData={product.raw_data}
        queryKey="synced-raw-data"
      />
    </div>
  );
}

interface RawDataSectionProps {
  title: string;
  icon: React.ReactNode;
  productId: string;
  fetchFn?: (productId: string) => Promise<unknown>;
  staticData?: unknown;
  queryKey: string;
}

function RawDataSection({
  title,
  icon,
  productId,
  fetchFn,
  staticData,
  queryKey,
}: RawDataSectionProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // React Query for live data
  const {
    data: liveData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [queryKey, productId],
    queryFn: () => fetchFn?.(productId),
    enabled: !!fetchFn && isExpanded, // Only fetch when expanded
    staleTime: 0, // Always fresh (no caching)
    gcTime: 0, // Don't keep in cache
  });

  const rawData = staticData || liveData;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(rawData, null, 2));
      toast.success(t('toast.success.jsonCopied'));
    } catch {
      toast.error(t('errors.copyFailed'));
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const jsonString = rawData ? JSON.stringify(rawData, null, 2) : '';
  const lineCount = jsonString.split('\n').length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="m-0">
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                className="flex items-center gap-2 text-left hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <span className="text-neutral-600 dark:text-neutral-400">{icon}</span>
                {title}
              </button>
            </CardTitle>
            {fetchFn && <Badge variant="secondary">Live</Badge>}
          </div>
          <div className="flex gap-2">
            {fetchFn && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isFetching}
                aria-label="Neu laden"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={isExpanded ? 'Einklappen' : 'Ausklappen'}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            {isExpanded && !!rawData && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                aria-label="JSON kopieren"
              >
                <Copy className="h-4 w-4 mr-1" />
                {t('common.copy')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-0">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8 text-neutral-600 dark:text-neutral-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Lade Daten vom Provider...</span>
            </div>
          )}

          {/* Error State */}
          {isError && !!error && (
            <div className="p-4">
              <Alert variant="error">
                <AlertDescription>
                  <strong>Fehler beim Laden:</strong>{' '}
                  {(error instanceof Error ? error.message : 'Unbekannter Fehler') as React.ReactNode}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Data Display */}
          {!isLoading && !isError && !!rawData && (
            <>
              {/* Info Bar */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800">
                <span className="text-sm text-neutral-600 dark:text-neutral-400 flex items-center gap-1">
                  <Braces className="h-4 w-4" />
                  {lineCount} Zeilen
                </span>
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  {fetchFn ? 'Live vom Provider' : 'Letzte Synchronisation'}
                </span>
              </div>

              {/* JSON Viewer */}
              <pre
                className="m-0 p-4 bg-neutral-900 text-neutral-100 overflow-auto rounded-b-lg"
                style={{
                  maxHeight: '500px',
                  fontSize: '0.75rem',
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                }}
              >
                {jsonString}
              </pre>
            </>
          )}

          {/* No Data */}
          {!isLoading && !isError && !rawData && (
            <div className="p-4 text-center text-neutral-600 dark:text-neutral-400">
              <span>{t('common.noData')}</span>
            </div>
          )}
        </CardContent>
      )}

      {!isExpanded && (
        <CardContent className="p-0">
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="w-full py-6 flex items-center justify-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors rounded-b-lg"
          >
            <ChevronDown className="h-4 w-4" />
            <span>
              {fetchFn
                ? 'Ausklappen, um Daten vom Provider zu laden'
                : 'Ausklappen, um Daten anzuzeigen'}
            </span>
          </button>
        </CardContent>
      )}
    </Card>
  );
}
